import { ChakraProvider } from '@chakra-ui/react'
import { Close as CloseIcon, Menu as MenuIcon } from '@mui/icons-material'
import { Stack, ThemeProvider, useMediaQuery } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import Lottie from 'react-lottie'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useGetPeerAssessmentQuery } from '../../app/services/api'
import colleague from '../../assets/lottie/colleague.json'
import loadingAnimation from '../../assets/lottie/loading.json'
import noDataAnimation from '../../assets/lottie/no-data.json'
import { appTheme } from '../../theme/theme'
import { useMixpanel } from '../../utils/MixpanelContext'
import { LeadershipValues } from './components/LeadershipValues'
import {
  MobileMenuButton,
  NoAuthorWrapper,
  PeerAssessmentContainer,
  PeerAssessmentMobileContainer,
} from './components/peer-assessment.style'
import { PeerAssessmentUsers } from './components/PeerAsessmentUsers'
import { PeerAssessmentArea } from './components/PeerAssessmentArea'
import { useHideChatlio } from '../../hooks/useHideChatlio'
import { DEFAULT_LOCALE } from '~/app/constants'
import { useLocale } from '~/hooks/useLocale'

function PeerAssessment() {
  const params = useParams<{ author: string; user: string }>()
  const [searchParams] = useSearchParams()
  const isEndJourney = useMemo(
    () => searchParams.get('type') === 'endjourney',
    [searchParams]
  )

  const author = params.author
  const { t, i18n } = useLocale()
  const currentUser = params.user
  const { pathname } = useLocation()
  const [language, setLanguage] = useState(i18n.language ?? DEFAULT_LOCALE)

  const { data, isFetching, isLoading, error, refetch } =
    useGetPeerAssessmentQuery(
      {
        author: author || '',
        language,
      },
      { skip: !author }
    )
  const isLeadershipPage = pathname.includes('/u/q/leadership-values')
  const mixpanel = useMixpanel()

  const currentRequest = data?.requests.find(
    (item) =>
      item.user._id === currentUser &&
      ((isEndJourney && item.type === 'peer-end-of-journey') ||
        (!isEndJourney && item.type === 'peer-assessment'))
  )

  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'))
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const handleChangeLanguage = (newLanguage: any) => {
    setLanguage(newLanguage)
    i18n
      .changeLanguage(newLanguage)
      .then(() => {
        refetch()
      })
      .catch((error) => {
        console.error('Error changing language:', error)
      })
    refetch()
  }

  useHideChatlio()

  useEffect(() => {
    if (data?.language && data?.language !== language) {
      handleChangeLanguage(data?.language)
    }
  }, [data?.language])

  // Hide Chatlio on meeting page
  useEffect(() => {
    mixpanel.track('peer_assessment_page_viewed', { author, currentUser })
  }, [])

  if (!author) {
    return (
      <NoAuthorWrapper>
        <Stack sx={{ pointerEvents: 'none' }}>
          <Lottie
            options={{
              animationData: noDataAnimation,
              autoplay: true,
              loop: true,
            }}
            width={200}
          />
        </Stack>
        <div className="info">
          <h1> No author </h1>
          <p>An author must be provided to access this page</p>
        </div>
      </NoAuthorWrapper>
    )
  }

  if ((isFetching || isLoading) && !data) {
    return (
      <NoAuthorWrapper>
        <Stack sx={{ pointerEvents: 'none' }}>
          <Lottie
            options={{
              animationData: loadingAnimation,
              autoplay: true,
              loop: true,
            }}
            width={200}
          />
        </Stack>
      </NoAuthorWrapper>
    )
  }

  if (!data) {
    mixpanel.track('peer_assessment_no_data', { author, currentUser, error })

    let errorTitle = 'No Data'
    let errorMessage = "We couldn't find any data for the provided email"

    if (error && 'status' in error && error.status === 'FETCH_ERROR') {
      const originalError = error.error
      if (
        originalError &&
        typeof originalError === 'string' &&
        originalError.includes('Failed to fetch')
      ) {
        errorTitle = 'Network Error'
        errorMessage =
          "It seems like your corporate IT system is blocking the request. Please contact your IT administrator to help whitelist the '*.hupo.co' domain or try accessing the link using your personal PC or phone."
      }
    } else if (error) {
      errorTitle = 'Error Loading Data'
      errorMessage =
        (error as any)?.data?.message ||
        'An error occurred while loading the data. Please try again later.'
    }

    return (
      <NoAuthorWrapper>
        <Stack sx={{ pointerEvents: 'none' }}>
          <Lottie
            options={{
              animationData: noDataAnimation,
              autoplay: true,
              loop: true,
            }}
            width={200}
          />
        </Stack>
        <div className="info">
          <h1>{errorTitle}</h1>
          <p>{errorMessage}</p>
        </div>
      </NoAuthorWrapper>
    )
  }
  mixpanel.track('peer_assessment_data_loaded', { author, currentUser })

  return (
    <PeerAssessmentContainer>
      {isMobile && (
        <MobileMenuButton
          onClick={handleMobileMenuToggle}
          sx={{
            right: !isMobileMenuOpen ? 'unset' : '16px',
          }}
        >
          {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </MobileMenuButton>
      )}
      {(!isMobile || isMobileMenuOpen) && (
        <PeerAssessmentUsers
          company={data?.company.name}
          requests={data?.requests}
          isMobileMenuOpen={isMobileMenuOpen}
          language={language}
          handleChangeLanguage={handleChangeLanguage}
        />
      )}
      {isMobile ? (
        <PeerAssessmentMobileContainer>
          {currentRequest ? (
            <PeerAssessmentArea
              data={currentRequest}
              company={data?.company}
              values={data?.leadershipValues.values}
              isMobileMenuOpen={isMobileMenuOpen}
              isEndJourney={isEndJourney}
            />
          ) : isLeadershipPage ? (
            <LeadershipValues data={data} />
          ) : (
            <NoAuthorWrapper>
              <Stack className="lottie">
                <Lottie
                  options={{
                    animationData: colleague,
                    autoplay: true,
                    loop: true,
                  }}
                  height={300}
                />
              </Stack>
              <div className="info">
                <h1
                  style={{
                    maxWidth: '500px',
                    fontWeight: 400,
                  }}
                >
                  {t('select_your_colleague_to_start')}
                </h1>
              </div>
            </NoAuthorWrapper>
          )}
        </PeerAssessmentMobileContainer>
      ) : (
        <>
          {currentRequest ? (
            <PeerAssessmentArea
              data={currentRequest}
              company={data?.company}
              values={currentRequest.values ?? data?.leadershipValues.values}
              isMobileMenuOpen={isMobileMenuOpen}
              isEndJourney={isEndJourney}
            />
          ) : isLeadershipPage ? (
            <LeadershipValues data={data} />
          ) : (
            <NoAuthorWrapper>
              <Stack sx={{ pointerEvents: 'none' }}>
                <Lottie
                  options={{
                    animationData: colleague,
                    autoplay: true,
                    loop: true,
                  }}
                  height={300}
                />
              </Stack>
              <div className="info">
                <h1
                  style={{
                    maxWidth: '500px',
                    fontWeight: 400,
                  }}
                >
                  {t('select_your_colleague_to_start')}
                </h1>
              </div>
            </NoAuthorWrapper>
          )}
        </>
      )}
    </PeerAssessmentContainer>
  )
}

export function PeerAssessmentPage() {
  return (
    <ThemeProvider theme={appTheme}>
      <ChakraProvider>
        <PeerAssessment />
      </ChakraProvider>
    </ThemeProvider>
  )
}
